import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Cable,
  Router,
  Radio,
  Wrench,
  Shield,
  Zap,
  ArrowRight,
  Copy,
  Check,
  MessageSquare,
  Sparkles,
  Building2,
  Globe,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import ChatWidget from "@/components/ChatWidget";
import { API } from "@/lib/api";
import { LANDING } from "@/constants/testIds";

const PRODUCT_CATEGORIES = [
  {
    icon: Cable,
    title: "Fiber Optic & FTTH",
    desc: "Kabel FO (ADSS, Drop Core, GYXTW), OLT & ONU FASTLINK, ODP/ODC/ODF, Splitter PLC",
    accent: "from-red-500/10 to-red-500/5",
  },
  {
    icon: Router,
    title: "Networking & LAN",
    desc: "UTP/FTP Cat 5E & Cat 6, Media Converter FX.LINK, HTB-3100, LAN accessories",
    accent: "from-rose-500/10 to-rose-500/5",
  },
  {
    icon: Radio,
    title: "MATV & CATV",
    desc: "Amplifier FTA/FSA, Node 2/4 Way, Passive Node WDM, Tap Outdoor, Splitter",
    accent: "from-red-500/10 to-orange-500/5",
  },
  {
    icon: Wrench,
    title: "Tools & Aksesoris",
    desc: "Fusion Splicer JILONG, Cleaver KL-23F, Joint Closure, Optical Transmitter",
    accent: "from-rose-500/10 to-red-500/5",
  },
];

const STATS = [
  { value: "23+", label: "Tahun pengalaman" },
  { value: "6", label: "Kantor & cabang" },
  { value: "1000+", label: "ISP & mitra" },
  { value: "24/7", label: "Support online" },
];

function EmbedCodeBlock() {
  const [copied, setCopied] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const code = `<!-- Falcom Chatbot Widget -->
<script src="${backendUrl.replace(/\/$/, "")}/embed.js" defer></script>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (e) {
      // Fallback for sandboxed environments
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (_) {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Kode berhasil disalin");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative bg-slate-900 rounded-2xl p-5 border border-slate-800">
      <button
        data-testid={LANDING.copyEmbedBtn}
        onClick={copy}
        className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium transition-colors"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Tersalin" : "Salin"}
      </button>
      <pre
        data-testid={LANDING.embedCode}
        className="text-xs sm:text-sm text-slate-100 overflow-x-auto font-mono leading-relaxed pt-6"
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function Landing() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetch(`${API}/config`).then((r) => r.json()).then(setConfig).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 relative">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#DC2626] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-bold text-[15px] leading-none tracking-tight">
                Falcom Technology
              </div>
              <div className="text-[10px] text-slate-500 leading-none mt-1">
                PT Mitra Kabel Indonesia
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              data-testid={LANDING.adminLink}
              to="/admin"
              className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors font-medium"
            >
              Admin
            </Link>
            <a
              href={config?.whatsapp_link || "https://wa.me/628113195050"}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm bg-[#25D366] hover:bg-[#1EBE5A] text-white px-3 py-2 rounded-lg font-medium transition-colors hidden sm:inline-block"
            >
              WhatsApp Kami
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 sm:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1 text-xs font-medium text-red-700 mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Chatbot AI 24/7 untuk Falcom Technology — Since 2003
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900">
              Jawab pelanggan otomatis.
              <br />
              <span className="text-[#DC2626]">Tangkap leads</span> tanpa henti.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              Chatbot cerdas untuk website distribusi Fiber Optic, FTTH, LAN &
              MATV/CATV. Menjawab pertanyaan produk Falcom (kabel FO, OLT/ONU FASTLINK,
              splicer JILONG, amplifier CATV) — dan otomatis menangkap calon
              pelanggan ke inbox Anda.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid={LANDING.openChatCta}
                onClick={() => {
                  const btn = document.querySelector(`[data-testid="chat-widget-launcher"]`);
                  btn?.click();
                }}
                className="group inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white px-6 py-3 rounded-xl font-medium text-sm transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                Coba Chatbot Sekarang
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <a
                href="#embed"
                data-testid={LANDING.heroCta}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-900 px-6 py-3 rounded-xl font-medium text-sm transition-colors"
              >
                Lihat Cara Embed
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
              {STATS.map((s) => (
                <div key={s.label} className="border-l-2 border-[#DC2626] pl-3">
                  <div className="font-display text-2xl font-bold text-slate-900">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_-15px_rgba(220,38,38,0.25)]">
            <img
              src="https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljJTIwc2VydmVyJTIwbmV0d29ya2luZ3xlbnwwfHx8fDE3ODQ5NjAyMzN8MA&ixlib=rb-4.1.0&q=85"
              alt="Fiber optic networking"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#DC2626]/80 via-[#DC2626]/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="text-[11px] uppercase tracking-widest opacity-80 font-medium">
                Distributor Resmi
              </div>
              <div className="font-display text-2xl font-bold mt-1 leading-tight">
                Fiber & Networking Solutions
              </div>
            </div>
          </div>
          {/* Floating mini-card */}
          <div className="absolute -left-4 sm:-left-8 top-1/4 bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.08)] p-4 max-w-[220px] hidden sm:block">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <div className="text-xs text-slate-500">Lead baru masuk</div>
            </div>
            <div className="font-semibold text-sm text-slate-900">Budi Santoso</div>
            <div className="text-xs text-slate-500">PT Nusantara Fiber</div>
            <div className="text-xs text-slate-400 mt-1">butuh 200 unit OLT GPON</div>
          </div>
        </motion.div>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-slate-200">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-[#DC2626] font-semibold mb-3">
            Katalog Produk
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Solusi lengkap untuk ISP, integrator & enterprise
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {PRODUCT_CATEGORIES.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className={`group bg-white border border-slate-200 hover:border-[#DC2626]/40 rounded-2xl p-6 transition-colors relative overflow-hidden`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${c.accent} opacity-0 group-hover:opacity-100 transition-opacity`} />
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-[#DC2626] text-white flex items-center justify-center mb-4">
                  <c.icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <div className="font-display font-bold text-lg text-slate-900">{c.title}</div>
                <div className="text-sm text-slate-500 mt-1.5 leading-relaxed">{c.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Embed section */}
      <section id="embed" className="max-w-7xl mx-auto px-6 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-widest text-[#DC2626] font-semibold mb-3">
              Instalasi 1 Menit
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Embed chatbot ke website Anda
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Cukup tempelkan satu baris kode ke halaman HTML website Anda,
              tombol chat mengambang akan muncul di pojok kanan bawah.
              Bekerja di WordPress, Shopify, HTML statis, atau framework apa saja.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "AI menjawab dalam Bahasa Indonesia",
                "Otomatis menangkap leads → email + database",
                "Eskalasi ke WhatsApp 1 klik",
                "Dashboard admin untuk kelola percakapan",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                  <div className="w-5 h-5 rounded-full bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#DC2626]" strokeWidth={3} />
                  </div>
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <EmbedCodeBlock />
            <div className="mt-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <Shield className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-900">
                Chatbot ini terintegrasi penuh dengan sistem lead management Falcom.
                Setiap lead yang masuk otomatis terkirim ke email admin dan tersimpan
                di dashboard.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-10">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#DC2626] flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-bold text-sm leading-none">Falcom Technology</div>
                <div className="text-[10px] text-slate-500 leading-none mt-1">
                  PT Mitra Kabel Indonesia
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
              Distributor & manufaktur peralatan telekomunikasi, TV kabel &
              internet sejak 2003. Cabang: Surabaya, Jakarta, Palembang,
              Pekanbaru, Samarinda, Makassar.
            </p>
            <a
              href="https://www.falcom-technology.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#DC2626] hover:underline mt-3 font-medium"
            >
              <Globe className="w-3 h-3" /> www.falcom-technology.com
            </a>
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">
              Kantor Surabaya
            </div>
            <div className="text-xs text-slate-600 leading-relaxed space-y-1">
              <div>Jl. Dukuh Kupang Barat I</div>
              <div>No. 180-182</div>
              <div>Surabaya – Jawa Timur 60225</div>
              <div className="pt-1 text-[#DC2626]">+62-31-5688209</div>
              <div className="text-[#DC2626]">+62-31-5614960</div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">
              Kantor Jakarta
            </div>
            <div className="text-xs text-slate-600 leading-relaxed space-y-1">
              <div>Bizpark Commercial Estate</div>
              <div>Pulogadung Block A7 No.15</div>
              <div>Jl. Raya Bekasi KM 21.5</div>
              <div>Cakung – Jakarta Timur 13260</div>
              <a
                href={config?.whatsapp_link || "https://wa.me/628113195050"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#DC2626] hover:underline block pt-1"
              >
                +62 811-3195-050 (WA)
              </a>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">
              Tim Internal
            </div>
            <Link
              to="/admin"
              className="text-sm text-slate-600 hover:text-[#DC2626] transition-colors flex items-center gap-1"
            >
              → Login Dashboard Admin
            </Link>
            <a
              href={config?.whatsapp_link || "https://wa.me/628113195050"}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1EBE5A] text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors"
            >
              <Phone className="w-3 h-3" /> Chat WhatsApp
            </a>
          </div>
        </div>
        <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-400">
          © 2026 PT Mitra Kabel Indonesia. All rights reserved.
        </div>
      </footer>

      <ChatWidget config={config} />
    </div>
  );
}
